import {tokens, colors, radii} from '@commercial-helios/ui/theme'
import {Root, Content, Trigger, Portal} from '@radix-ui/react-popover'
import ky from 'ky'
import React, {useState} from 'react'
import styled from 'styled-components'

import RichText from '@helios/shared/components/rich-text'
import {Search} from '@helios/shared/images/icons/search'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {inputStyles, Label} from '@helios/shared/styles/inputs'
import {Text, text} from '@helios/shared/styles/typography'
import {focusStyles} from '@helios/shared/styles/utils'

export type ClearbitCompany = {
    id: string
    domain: string
    logo: string
    name: string
}

interface Props {
    onChange: (newValue: string) => void
    onSelect: (company: ClearbitCompany) => void
    onFocus?: () => void
    value: string
    placeholder: string
    label?: string
    error?: string | null
    id?: string
}

const CompanyInput = ({label, placeholder, value, onChange, error, id, onSelect}: Props) => {
    const [dropdownCompanies, setDropdownCompanies] = useState<ClearbitCompany[]>([])
    const [open, setOpen] = useState<boolean>(false)
    const globalStrings = useGlobalStrings()
    const containerRef = React.useRef<HTMLDivElement>(null)
    const labelId = React.useId()

    React.useEffect(() => {
        setOpen(value.length > 2 && dropdownCompanies.length > 0)
    }, [value, dropdownCompanies])

    const fetchCompanies = async (value: string) => {
        try {
            const companies = await ky
                .get(`https://autocomplete.clearbit.com/v1/companies/suggest?query=${value}`)
                .json<ClearbitCompany[]>()

            const companiesWithIDs = companies.map((company, i) => ({
                ...company,
                id: `${labelId}-${i}`,
            }))

            setDropdownCompanies(companiesWithIDs)
        } catch (error) {
            console.error(error)
        }
    }

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
        onChange(event.target.value)

        event.target.value.length > 2 ? fetchCompanies(value) : setDropdownCompanies([])
    }

    const handleClose = () => {
        setDropdownCompanies([])
    }

    return (
        <Wrapper>
            {label && <Label htmlFor={labelId}>{label}</Label>}

            <Root open={open}>
                <Trigger asChild>
                    <InputWrapper ref={containerRef}>
                        <SearchIcon aria-hidden />

                        <input
                            id={labelId}
                            type="search"
                            autoComplete="off"
                            value={value}
                            placeholder={placeholder}
                            onChange={handleChange}
                        />
                    </InputWrapper>
                </Trigger>

                <Portal container={containerRef.current}>
                    <StyledPopoverContent
                        onOpenAutoFocus={(event) => event.preventDefault()}
                        onInteractOutside={handleClose}
                        onEscapeKeyDown={handleClose}
                        asChild
                    >
                        <StyledList>
                            {dropdownCompanies.map((company) => {
                                return (
                                    <li key={company.id}>
                                        <StyledItem
                                            onClick={() => {
                                                onSelect?.(company)
                                                setDropdownCompanies([])
                                            }}
                                        >
                                            <img
                                                alt=""
                                                src={`${company.logo}?size=64`}
                                                width="32"
                                            />{' '}
                                            <span>{company.name}</span>
                                        </StyledItem>
                                    </li>
                                )
                            })}

                            <ClearbitCredits>
                                <RichText
                                    content={globalStrings.clearbitCredits}
                                    variant="label-base"
                                    color="lightMuted"
                                />
                            </ClearbitCredits>
                        </StyledList>
                    </StyledPopoverContent>
                </Portal>
            </Root>

            {error && (
                <Text variant="label-base" color="error">
                    {error}
                </Text>
            )}
        </Wrapper>
    )
}

const Wrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing8};
`

const iconSpacingLeft = tokens.spacing20
const iconSpacingRight = tokens.spacing12
const iconSize = '20px'

const InputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;

    & input {
        ${inputStyles}
        padding-left: calc(${iconSize} + ${iconSpacingLeft} + ${iconSpacingRight});
    }

    &[data-state='open'] input {
        ${focusStyles()};
        border-bottom-color: transparent;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }
`

const SearchIcon = styled(Search)`
    position: absolute;
    left: ${iconSpacingLeft};
    width: ${iconSize};
    height: ${iconSize};
    color: ${tokens.shade600};
    pointer-events: none;

    ${InputWrapper}:focus-within & {
        color: ${tokens.shade700};
    }
`

const StyledPopoverContent = styled(Content)`
    z-index: 1;
    width: var(--radix-popover-trigger-width);
    max-height: min(250px, 50vh);
    border: 1px solid ${colors.border.secondary};
    background: ${colors.background.default};
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    &[data-side='bottom'] {
        border-bottom-right-radius: ${radii.arc8};
        border-bottom-left-radius: ${radii.arc8};
    }

    &[data-side='top'] {
        border-top-left-radius: ${radii.arc8};
        border-top-right-radius: ${radii.arc8};
    }

    &[data-state='open'] {
        ${focusStyles()}
    }
`

const StyledList = styled.ul`
    display: grid;
    gap: ${tokens.spacing4};
    padding: ${tokens.spacing8} 0;
    margin: 0;
    list-style: none;
`

const StyledItem = styled.button`
    all: unset;
    width: 100%;
    box-sizing: border-box;
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    min-height: ${tokens.spacing48};
    padding: ${tokens.spacing8} ${tokens.spacing20} ${tokens.spacing8} ${tokens.spacing24};
    gap: ${tokens.spacing8};
    ${text({variant: 'paragraph-base'})};
    cursor: pointer;

    &:hover,
    &:focus {
        background-color: ${tokens.shade300};
    }

    &[aria-selected='true'] {
        background-color: hsl(211deg 10% 95%);
    }

    img {
        border-radius: ${radii.arc8};
    }
`

const ClearbitCredits = styled.li`
    padding: ${tokens.spacing8} ${tokens.spacing20};

    a {
        text-decoration: underline;
        ${text({variant: 'label-base', color: 'lightMuted'})}
    }
`

export default CompanyInput
